import React from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { formatIDR, formatShortIDR } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";

const fetcher = (url) => api.get(url).then((r) => r.data);
const COLORS = ["#22C55E", "#3B82F6", "#F59E0B", "#EF4444", "#A855F7", "#EC4899", "#14B8A6", "#84CC16", "#06B6D4"];

export default function Reports() {
  const { data: summary } = useSWR("/reports/summary", fetcher);

  const monthly = summary?.monthly || [];
  const cumulative = [];
  let running = 0;
  monthly.forEach((m) => {
    running += (m.income - m.expense);
    cumulative.push({ month: m.month, net: running });
  });

  const cats = (summary?.categories || []).slice(0, 8);

  return (
    <div className="animate-fade-up space-y-8">
      <div>
        <p className="label-caps">Analytics</p>
        <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-1">Financial Report</h1>
        <p className="text-sm text-muted-foreground mt-2">Ringkasan performa keuangan berdasarkan seluruh transaksi.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Kpi label="Cash Flow" value={formatIDR(summary?.cash_flow || 0)} testid="report-kpi-cashflow" />
        <Kpi label="Net Worth" value={formatIDR(summary?.net_worth || 0)} testid="report-kpi-networth" />
        <Kpi label="Portfolio" value={formatIDR(summary?.portfolio_value || 0)} testid="report-kpi-portfolio" />
        <Kpi label="Transaksi" value={summary?.transaction_count || 0} testid="report-kpi-count" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border bg-card p-5">
          <p className="label-caps mb-4">Income vs Expense (12 bulan)</p>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={formatShortIDR} />
                <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" fill="hsl(var(--gain))" name="Income" />
                <Bar dataKey="expense" fill="hsl(var(--loss))" name="Expense" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-border bg-card p-5">
          <p className="label-caps mb-4">Net Worth Trajectory</p>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={cumulative}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={formatShortIDR} />
                <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Line type="monotone" dataKey="net" stroke="hsl(var(--foreground))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="border border-border bg-card p-5">
        <p className="label-caps mb-4">Pengeluaran per Kategori</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-72">
            {cats.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Belum ada data</div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={cats} dataKey="amount" nameKey="category" outerRadius={100}>
                    {cats.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="space-y-2">
            {cats.map((c, i) => (
              <div key={c.category} className="flex items-center justify-between border-b border-border py-2" data-testid={`report-cat-${c.category}`}>
                <div className="flex items-center gap-3">
                  <span className="h-3 w-3" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="text-sm">{c.category}</span>
                </div>
                <span className="font-mono text-sm">{formatIDR(c.amount)}</span>
              </div>
            ))}
            {cats.length === 0 && <p className="text-sm text-muted-foreground">Tidak ada pengeluaran tercatat.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="border border-border bg-card p-5" data-testid={testid}>
      <p className="label-caps">{label}</p>
      <p className="metric-num text-3xl mt-3">{value}</p>
    </div>
  );
}
